/**
 * AuthContext - Authentication state management
 * Manages user authentication state, tokens, and provides login/logout methods
 *
 * Password login, silent refresh, /me, and logout run against the platform
 * SSO surface (blazorly-agent /auth, see lib/authClient.ts) so one login is
 * shared across agent, crew, and builder. Backend-only flows that the SSO
 * surface does not host yet — OAuth login buttons and 2FA — keep calling the
 * backend /api/auth endpoints and continue to work as before.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { config } from '../config';
import {
	getAuthClient,
	getStoredToken,
	clearStoredToken,
	TOKEN_KEY,
	SESSION_KEY,
	notifySessionEnded,
	onSessionEnded,
	refreshAccessToken,
	isUnauthorizedError,
} from '../lib/authClient';
import { isAuthHandoffPath } from '../lib/authRedirect';
import { markTiming, measureTiming } from '../lib/bootstrapTimings';

interface User {
	id: string;
	email: string;
	displayName: string;
	username: string | null;
	avatarUrl: string | null;
	bio: string | null;
	provider: string;
	emailVerified: boolean;
	twoFactorEnabled: boolean;
	createdAt: string;
}

interface AuthContextValue {
	user: User | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	error: string | null;
	/** Full URL of the identity SPA's sign-in page, with a return redirect. */
	loginUrl: (returnTo?: string) => string;
	logout: () => Promise<void>;
	refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
	children: React.ReactNode;
}

/**
 * Normalize the platform user shape into the builder's User interface.
 * The SSO surface returns { id, email, displayName, createdAt(unix seconds) }
 * plus authorization context; fields it does not carry yet resolve to safe
 * defaults rather than undefined so existing UI keeps rendering.
 */
function toBuilderUser(raw: {
	id: string;
	email: string;
	displayName: string | null;
	createdAt?: number | string;
	username?: string | null;
	avatarUrl?: string | null;
	provider?: string;
	emailVerified?: boolean;
	twoFactorEnabled?: boolean;
}): User {
	const createdAt = typeof raw.createdAt === 'number'
		? new Date(raw.createdAt * 1000).toISOString()
		: String(raw.createdAt ?? new Date().toISOString());
	return {
		id: raw.id,
		email: raw.email,
		// Platform users can have a NULL display_name (backend OAuth signups);
		// fall back to the email local-part so UI labels always render. The
		// final ?? guards noUncheckedIndexedAccess on split()[0].
		displayName: raw.displayName ?? raw.email.split('@')[0] ?? raw.email,
		username: raw.username ?? null,
		avatarUrl: raw.avatarUrl ?? null,
		bio: null,
		provider: raw.provider ?? 'email',
		// The platform SSO surface is the authority for login; it does not gate
		// on email verification, so builder banners must not nag about it.
		emailVerified: raw.emailVerified ?? true,
		twoFactorEnabled: raw.twoFactorEnabled ?? false,
		createdAt,
	};
}

export function AuthProvider({ children }: AuthProviderProps) {
	const [user, setUser] = useState<User | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	/**
	 * Fetch current user from the platform SSO surface
	 */
	const fetchUser = useCallback(async (token: string): Promise<User | null> => {
		try {
			const platformUser = await getAuthClient().me(token);
			return toBuilderUser(platformUser);
		} catch (err) {
			if (!isUnauthorizedError(err)) throw err;
			// A rejected token may just be expired — try one silent refresh via
			// the shared cookie before giving up (agent/crew webs pattern).
			const replacement = await refreshAccessToken();
			if (replacement) {
				const refreshed = await getAuthClient().me(replacement);
				return toBuilderUser(refreshed);
			}
			if (getStoredToken()) throw err;
			console.error('Failed to fetch user:', err);
			return null;
		}
	}, []);

	/**
	 * Load user on mount. If no local token exists, first try a silent
	 * refresh — a session created on agent/crew (shared cookie) logs the
	 * builder in without a second prompt.
	 */
	useEffect(() => {
		const loadUser = async () => {
			setIsLoading(true);
			setError(null);
			markTiming('auth:start');

			for (let attempt = 0; attempt < 5; attempt++) {
				try {
					// One /me roundtrip validates the stored token AND returns the
					// user (fetchUser retries once via silent refresh on 401). A
					// separate restoreSession() validation call used to double
					// this on every page load, gating all rendering behind it.
					let token = getStoredToken();
					if (!token) token = await refreshAccessToken();
					if (!token) {
						setUser(null);
						setIsLoading(false);
						measureTiming('auth:resolved anonymous', 'auth:start');
						return;
					}

					const userData = await fetchUser(token);
					setUser(userData);
					setIsLoading(false);
					measureTiming('auth:resolved', 'auth:start');
					return;
				} catch (err) {
					console.error('Failed to load user:', err);
					setError(err instanceof Error ? err.message : 'Failed to load user');
					if (!getStoredToken()) {
						setUser(null);
						setIsLoading(false);
						measureTiming('auth:resolved anonymous', 'auth:start');
						return;
					}
					await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
				}
			}
		};

		loadUser();
	}, [fetchUser]);

	useEffect(() => {
		return onSessionEnded(() => {
			clearStoredToken();
			setUser(null);
		});
	}, []);

	/**
	 * Sign-in lives on the platform identity service; return there with a
	 * redirect back to the requested builder path.
	 */
	const loginUrl = useCallback((returnTo?: string) => {
		const origin = window.location.origin;
		let target = returnTo ?? origin;
		try {
			const url = new URL(target, origin);
			target = isAuthHandoffPath(url.pathname) ? `${origin}/` : url.href;
		} catch {
			target = `${origin}/`;
		}
		return `${config.authAppUrl}/login?redirect=${encodeURIComponent(target)}`;
	}, []);

	/**
	 * Logout user and revoke the platform session
	 */
	const logout = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const token = getStoredToken();
			if (token) {
				try {
					await getAuthClient().logout(token);
				} catch (err) {
					// Don't throw on logout error - still clear local state
					console.error('Failed to revoke session on server:', err);
				}
			}

			clearStoredToken();
			setUser(null);
			notifySessionEnded();
		} catch (err) {
			console.error('Logout failed:', err);
			setError(err instanceof Error ? err.message : 'Logout failed');
		} finally {
			setIsLoading(false);
		}
	}, []);

	/**
	 * Refresh user data from the platform SSO surface
	 */
	const refreshUser = useCallback(async () => {
		const token = getStoredToken();
		if (!token) {
			setUser(null);
			return;
		}

		setIsLoading(true);
		setError(null);

		try {
			const userData = await fetchUser(token);
			if (!userData) {
				clearStoredToken();
				setUser(null);
			} else {
				setUser(userData);
			}
		} catch (err) {
			console.error('Failed to refresh user:', err);
			setError(err instanceof Error ? err.message : 'Failed to refresh user');
			setUser(null);
			clearStoredToken();
		} finally {
			setIsLoading(false);
		}
	}, [fetchUser]);

	const value: AuthContextValue = {
		user,
		isAuthenticated: !!user,
		isLoading,
		error,
		loginUrl,
		logout,
		refreshUser,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 */
export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within AuthProvider');
	}
	return context;
}

/**
 * Helper to set auth token (used by OAuth callback)
 */
export function setAuthToken(token: string, sessionId: string) {
	localStorage.setItem(TOKEN_KEY, token);
	localStorage.setItem(SESSION_KEY, sessionId);
}

/**
 * Canonical token reader. Delegates to lib/authClient so the storage key and
 * the read path have one implementation.
 */
export function getAuthToken(): string | null {
	return getStoredToken();
}
